import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { COLORS } from '../../styles/tokens';
import { useUnreadCount } from '../../hooks/useUnreadCount';
import TabBadge from '../common/TabBadge';

const ICONS = {
  map:    <><path d="M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3z" /><path d="M9 3v15M15 6v15" /></>,
  truck:  <><path d="M2 6h11v10H2zM13 9h4l3 3v4h-7" /><circle cx="6" cy="17.5" r="1.8" /><circle cx="16.5" cy="17.5" r="1.8" /></>,
  wallet: <><rect x="3" y="6" width="18" height="14" rx="3" /><path d="M3 10h18M7 3h10" /></>,
  bell:   <path d="M6 17V11a6 6 0 1112 0v6l1.5 2h-15zM10 21h4" />,
  user:   <><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 3.5-6.5 8-6.5s8 2.5 8 6.5" /></>,
};

function TabIcon({ name }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {ICONS[name]}
    </svg>
  );
}

const TABS = [
  { key: 'new',      label: 'Oppdrag',      icon: 'map',    to: '/driver?tab=new' },
  { key: 'mine',     label: 'Mine oppdrag', icon: 'truck',  to: '/driver?tab=mine' },
  { key: 'earnings', label: 'Inntekt',      icon: 'wallet', to: '/driver/earnings' },
  { key: 'notifications', label: 'Varsler',   icon: 'bell',   to: '/driver/varsler' },
  { key: 'profile',  label: 'Profil',       icon: 'user',   to: '/driver/profil' },
];

// Bunnmeny for sjåførappen på mobil. Aktiv fane utledes av adressen.
function useNarrow() {
  const [narrow, setNarrow] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setNarrow(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return narrow;
}

// onHeight får menyens faktiske høyde (px), eller 0 når den ikke vises, så arket kan holde seg over den
export default function DriverTabBar({ onHeight }) {
  const narrow = useNarrow();
  const barRef = useRef(null);
  const [barPx, setBarPx] = useState(0);
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { unreadCount } = useUnreadCount();

  useEffect(() => {
    const el = barRef.current;
    if (!narrow || !el) { setBarPx(0); if (onHeight) onHeight(0); return undefined; }
    const report = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      setBarPx(h);
      if (onHeight) onHeight(h);
    };
    report();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => ro.disconnect();
  }, [narrow, onHeight]);

  if (!narrow) return null;

  let active = 'new';
  if (pathname.startsWith('/driver/earnings')) active = 'earnings';
  else if (pathname.startsWith('/driver/varsler')) active = 'notifications';
  else if (pathname.startsWith('/driver/profil')) active = 'profile';
  else if (pathname.startsWith('/driver/orders')) active = 'profile';
  else if (pathname.startsWith('/driver/active')) active = 'mine';
  else if (params.get('active') || params.get('tab') === 'mine') active = 'mine';

  // Menyen er festet til bunnen av skjermen uansett scrolling; avstandsholderen
  // beholder plassen den ellers ville tatt i sidens flyt.
  return (
    <>
    <div aria-hidden="true" style={{ height: barPx, flexShrink: 0 }} />
    <nav ref={barRef} aria-label="Hovedmeny" style={styles.bar}>
      {TABS.map(t => {
        const on = t.key === active;
        return (
          <button key={t.key} onClick={() => navigate(t.to)} aria-current={on ? 'page' : undefined}
                  aria-label={t.key === 'notifications' && unreadCount > 0 ? `Varsler, ${unreadCount} uleste` : undefined}
                  style={{ ...styles.tab, color: on ? COLORS.textDark : COLORS.textLight }}>
            <span style={{ ...styles.pill, background: on ? COLORS.accent : 'transparent' }}>
              <TabIcon name={t.icon} />
              {t.key === 'notifications' && <TabBadge count={unreadCount} />}
            </span>
            <span style={{ fontWeight: on ? 700 : 600, whiteSpace: 'nowrap' }}>{t.label}</span>
          </button>
        );
      })}
    </nav>
    </>
  );
}

const styles = {
  bar: {
    position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 300,
    display: 'flex', background: COLORS.white, borderTop: `1px solid ${COLORS.border}`,
    padding: '8px 8px max(10px, env(safe-area-inset-bottom))',
  },
  tab: {
    flex: 1, background: 'none', border: 'none', cursor: 'pointer',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
    fontSize: 11, fontFamily: 'inherit', padding: 0,
  },
  pill: {
    position: 'relative', width: 52, height: 30, borderRadius: 15,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
};
